/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import getTransferPath from "util/transfers";
import moment from "moment-timezone";
import { bool, func, arrayOf, shape, string, number } from "prop-types";

import { Dashboard, Text, Toast, useIsMobile } from "@ui-kit/components/index.es";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as campaignsActions, selectors as campaignsSelectors } from "reducers/campaigns";
import { actions as desktopActions, selectors as desktopSelectors } from "reducers/desktop";
import { actions as statusActions, selectors as statusSelectors } from "reducers/status";
import { actions as transactionsActions } from "reducers/transactions";
import { actions as widgetsActions, selectors as widgetsSelectors } from "reducers/widgets";
import { selectors as fingerprintSelectors } from "reducers/fingerprint";
import { selectors as pushNotificationsSelectors } from "reducers/pushNotifications";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as settingsSelectors } from "reducers/settings";
import { actions as transferActions } from "reducers/transfer";
import { actions as loginActions, selectors as loginSelectors } from "reducers/login";
import { actions as notificationActions } from "reducers/notification";
import * as configUtil from "util/config";
import * as i18n from "util/i18n";
import { maskedAccountNumber, getRutPathAccount } from "util/accounts";
import { getBankAccountDetailPath } from "util/v3UxConfig";
import * as metrocorpMiddleware from "middleware/metrocorp";
import { getMetrocorpConsultPath } from "util/metrocorpRoutes";
import { isUxMetrocorpWidgetEnabled } from "util/v3ShellLayoutSync";
import { getMultipleTransfersI18nKey } from "util/multiple-transfers";
import { getTransferActivityName } from "util/transferInternalTitleLabels";

import { resizableRoute } from "pages/_components/Resizable";
import BiometricIdentificationV3 from "v3/widgets/BiometricIdentificationV3";
import PushNotification from "pages/desktop/widgets/PushNotification";
import { isEnabledApi5 } from "util/fundcorp";
import { actions as layoutActions } from "reducers/layout";
import firstNameFromFullName from "util/v3UserGreeting";
import buildDashboardShortcuts from "v3/utils/dashboard/buildDashboardShortcuts";
import {
    buildDashboardMultiSectionSections,
    mapMetrocorpHoldingsToWidgetAccounts,
    metrocorpWidgetNavigation,
    getDashboardV3WidgetLabelKey,
    getOrderedMultiSectionLayoutEntries,
    TITLE_KEY_TO_WIDGET_ID,
    WIDGET_ID_TO_TITLE_KEY,
} from "v3/utils/dashboard/multiSectionMappers";
import * as v3UiKit from "v3/v3UiKitI18n";

function getDesktopWidgetMaxItems() {
    return configUtil.getInteger("dektop.max.items.widget", 3);
}

/**
 * Payload para precargar cuenta de débito en el reducer de transferencias
 */
function dashboardTransferDebitSummary(accountId) {
    return { debitAccount: { value: accountId } };
}

function mapAccountsForDashboard(rawAccounts) {
    if (!Array.isArray(rawAccounts) || rawAccounts.length === 0) {
        return [];
    }
    return rawAccounts.map((account) => {
        const numberDisplay = maskedAccountNumber(account.number);
        const productName = account.productAlias || numberDisplay;
        const productType =
            account.productType === "CA"
                ? i18n.get("accounts.productType.savings")
                : i18n.get("accounts.productType.checking");
        const currencyKey = String(account.currency ?? "ARS");
        const currencyLabel = i18n.get(`currency.label.${currencyKey}`);
        return {
            id: String(account.idProduct),
            accountName: productName,
            accountType: productType,
            accountNumber: numberDisplay,
            currency: currencyLabel,
            amount: account.balance ?? account.countableBalance ?? 0,
            isFavorite: Boolean(account.favorite),
        };
    });
}

/**
 * Extrae el nombre del beneficiario/destinatario a partir de los datos de la transacción.
 * Lógica alineada con `TransactionCustomInfo` del legacy.
 */
function getPendingBeneficiaryName(idActivity, data) {
    if (!data || !idActivity) {
        return "";
    }
    const transferActivities = [
        "transfers.thirdParties.send",
        "transfersCVU.thirdParties.send",
        "transfers.thirdParties.tad.send",
        "transfers.toContact.send",
        "transfersCVU.toContact.send",
        "transfers.toContact.tad.send",
        "scheduled.transactions.cancel",
    ];
    if (transferActivities.includes(idActivity)) {
        return data.clientUser?.firstName || "";
    }
    const echeqBeneficiaryActivities = [
        "emit.echeq.activity",
        "external.emit.echeq.activity",
        "endorse.echeq.activity",
        "cesion.echeq.activity",
    ];
    if (echeqBeneficiaryActivities.includes(idActivity)) {
        return data.beneficiaryName || "";
    }
    if (idActivity === "emit.massive.echeqs.send" || idActivity === "external.emit.massive.echeq.activity") {
        const ref = data?.reference;
        const batchId = data?.batchId;
        if (ref && batchId != null) {
            return `${ref} (${batchId})`;
        }
        return ref || (batchId != null ? String(batchId) : "");
    }
    if (idActivity === "send.external.transaction" || idActivity === "ecomex.send.accept.transaction") {
        const td = data.transactionData || {};
        return td.beneficiaryName || td.nombreBeneficiario || data.beneficiaryName || data.nombreBeneficiario || "";
    }
    return "";
}

/**
 * Filas del widget `pendingTransactions` → contrato `DashboardPendingTransaction` del UI kit.
 * Misma transformación base que `PendingTransactions.jsx` (currency/quantity desde `transactionAmounts`).
 *
 * @param {object[]} list — `data.pendingTransactions` del reducer widgets
 * @param {number} maxItems
 */
function mapPendingWidgetRowsToDashboard(list, maxItems) {
    if (!Array.isArray(list) || list.length === 0) {
        return [];
    }
    return list.slice(0, maxItems).map((row) => {
        const transaction = row.transaction || {};
        const transactionAmounts = row.transactionAmounts || {};
        const currencyCode = Object.keys(transactionAmounts)[0];
        const quantity = Object.values(transactionAmounts)[0];
        const { activityName, idActivity, idTransaction, creationDateTime, data, idTransactionStatus } = transaction;
        let transactionName = activityName || "";
        if (idActivity === "transfer.multiple.send") {
            transactionName = i18n.get(getMultipleTransfersI18nKey("activities.transfer.multiple.send"));
        } else {
            transactionName = getTransferActivityName(idActivity, transactionName);
        }
        const creationDate = creationDateTime
            ? moment(creationDateTime)
                  .tz("America/Argentina/Buenos_Aires")
                  .format("DD/MM/YYYY HH:mm:ss")
            : "";
        const beneficiary = getPendingBeneficiaryName(idActivity, data);
        const extraData = beneficiary
            ? React.createElement(
                  Text,
                  { variant: "captionBold", color: "text" },
                  `${i18n.get("transaction.list.header.to")} ${beneficiary}`,
              )
            : null;
        return {
            id: String(idTransaction),
            creationDate,
            transactionName,
            extraData,
            currency: currencyCode ? i18n.get(`currency.label.${currencyCode}`) : undefined,
            amount: quantity,
            variant: idTransactionStatus === "DRAFT" ? "draft" : "default",
        };
    });
}

const WIDGET_ICON_MAP = {
    accounts: "AddAccount",
    pendingTransactions: "Clock",
    echeqsReceived: "EmitCheq",
    funds: "StocksUp",
    founds: "StocksUp",
    deposits: "StocksUp",
    financing: "Briefcase",
    metrocorp: "GraphLine",
    scheduledTransactions: "Calendar",
    exchangeRates: "ArrowUpRight",
};

const V3_FIXED_WIDGETS = new Set(["accounts", "pendingTransactions"]);

function mapAvailableWidgetsToEditItems(availableWidgets, layout) {
    if (!Array.isArray(availableWidgets) || availableWidgets.length === 0) {
        return [];
    }
    const widgetMap = {};
    availableWidgets.forEach((widget) => {
        if (!V3_FIXED_WIDGETS.has(widget.id)) {
            widgetMap[widget.id] = widget;
        }
    });

    const sortedLayout = [...(layout || [])].sort((a, b) => a.row - b.row);
    const selectedItems = sortedLayout
        .filter((w) => widgetMap[w.id])
        .map((w) => ({
            id: w.id,
            icon: WIDGET_ICON_MAP[w.id] || "ArrowUpRight",
            label: i18n.get(getDashboardV3WidgetLabelKey(w.id)) || w.id,
            selected: true,
        }));

    const activeIds = new Set(sortedLayout.map((w) => w.id));
    const unselectedItems = availableWidgets
        .filter((widget) => !V3_FIXED_WIDGETS.has(widget.id) && !activeIds.has(widget.id))
        .map((widget) => ({
            id: widget.id,
            icon: WIDGET_ICON_MAP[widget.id] || "ArrowUpRight",
            label: i18n.get(getDashboardV3WidgetLabelKey(widget.id)) || widget.id,
            selected: false,
        }));

    return [...selectedItems, ...unselectedItems];
}

function DashboardV3({
    location,
    isMobileNative,
    dispatch,
    userFullName,
    userId,
    activeEnvironment,
    isRetailEnvironment,
    deviceWithFingerprint,
    biometryCancelled,
    widgetAccountsList,
    widgetPendingList,
    loadingWidgetAccounts,
    loadingWidgetPending,
    campaigns,
    desktopLayout,
    desktopAvailableWidgets,
    multiSectionWidgets,
    hideBalance,
    dataAccountsFundcorp,
    showMaintenanceToast,
    isComeFromLogin,
    daysToExpireCredential,
    desktopCredentialsNotificationShowed,
}) {
    const { isMobile } = useIsMobile();
    const maxItems = getDesktopWidgetMaxItems();

    const enableHideAmounts = configUtil.getBoolean("frontend.hide.accounts.balance.funcionalities", false);
    const hideAmounts = enableHideAmounts && Boolean(hideBalance) && isMobile;

    const [showWelcomeToast, setShowWelcomeToast] = useState(false);
    const isComeFromLoginRef = useRef(isComeFromLogin);

    useEffect(() => {
        if (isComeFromLoginRef.current && showMaintenanceToast) {
            setShowWelcomeToast(true);
        }
        isComeFromLoginRef.current = false;
    }, [showMaintenanceToast]);

    useEffect(() => {
        if (isComeFromLogin) {
            isComeFromLoginRef.current = true;
            const timer = setTimeout(() => {
                dispatch(statusActions.resetComeFromLogin());
            }, 1000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [isComeFromLogin, dispatch]);

    useEffect(() => {
        if (desktopCredentialsNotificationShowed) {
            return undefined;
        }
        const passwordNotifyDays = configUtil.getInteger("login.password.validity.notify.days", 10);
        const userpassNotifyDays = configUtil.getInteger("login.userpass.validity.notify.days", 10);
        const userNameAboutToExpire = parseInt(daysToExpireCredential?.userpassDaysToExpire, 10) <= userpassNotifyDays;
        const passwordAboutToExpire = parseInt(daysToExpireCredential?.passwordDaysToExpire, 10) <= passwordNotifyDays;

        const timers = [];
        if (userNameAboutToExpire) {
            timers.push(
                setTimeout(() => {
                    dispatch(
                        notificationActions.showNotification(
                            daysToExpireCredential.desktopUserpassMessage,
                            "warning",
                            ["desktop"],
                            i18n.get("enrollment.stepIRS.warning.title"),
                        ),
                    );
                }, 1000),
            );
            if (passwordAboutToExpire) {
                timers.push(
                    setTimeout(() => {
                        dispatch(
                            notificationActions.showNotification(
                                daysToExpireCredential.desktopPasswordMessage,
                                "warning",
                                ["desktop"],
                                i18n.get("enrollment.stepIRS.warning.title"),
                            ),
                        );
                    }, 7000),
                );
            }
        } else if (passwordAboutToExpire) {
            timers.push(
                setTimeout(() => {
                    dispatch(
                        notificationActions.showNotification(
                            daysToExpireCredential.desktopPasswordMessage,
                            "warning",
                            ["desktop"],
                            i18n.get("enrollment.stepIRS.warning.title"),
                        ),
                    );
                }, 1000),
            );
        }
        dispatch(loginActions.setDesktopCredentialNotificationShowed());
        return () => timers.forEach(clearTimeout);
    }, []); // run once on mount — mirrors legacy Desktop.componentDidMount

    const handleCloseMaintenanceToast = useCallback(() => {
        setShowWelcomeToast(false);
    }, []);

    const handleDisableToastNews = useCallback(() => {
        dispatch(desktopActions.disableToastNewsRequest());
    }, [dispatch]);

    const metrocorpWidgetEnabled = isUxMetrocorpWidgetEnabled();
    const [metrocorpHoldings, setMetrocorpHoldings] = useState(null);
    const [metrocorpHoldingsLoading, setMetrocorpHoldingsLoading] = useState(metrocorpWidgetEnabled);
    const [editWidgets, setEditWidgets] = useState([]);
    const editWidgetsRef = useRef(editWidgets);
    editWidgetsRef.current = editWidgets;
    const initialMultiSectionLoaded = useRef(false);
    const skipGetDataAfterEdit = useRef(false);

    useEffect(() => {
        if (userId) {
            dispatch(widgetsActions.listRequest("accounts", userId));
            dispatch(widgetsActions.listRequest("pendingTransactions", userId));
        }
        dispatch(desktopActions.loadLayoutRequest());
        dispatch(campaignsActions.loadCampaignList("desktop-header"));
        dispatch(statusActions.saveLastHref(location));
        dispatch(transactionsActions.saveTransactionRoute("/v3/dashboard"));
        dispatch(accountsActions.accountsLastHref("/v3/dashboard"));
    }, [dispatch, userId, location]);

    const canOperateFunds = Boolean(activeEnvironment?.permissions?.fundcorpOperations);
    const fundcorpFetched = useRef(false);
    useEffect(() => {
        if (canOperateFunds && !fundcorpFetched.current && !dataAccountsFundcorp?.accountsArray?.length) {
            fundcorpFetched.current = true;
            dispatch(accountsActions.listAccountsFundcorp());
        }
    }, [dispatch, dataAccountsFundcorp, canOperateFunds]);

    useEffect(() => {
        dispatch(layoutActions.setScopes(["desktop"]));
        return () => {
            dispatch(layoutActions.setScopes([]));
        };
    }, [dispatch]);

    const multiSectionLayoutEntries = useMemo(() => getOrderedMultiSectionLayoutEntries(desktopLayout), [
        desktopLayout,
    ]);

    const metrocorpHoldingsInFlight = useRef(false);
    const fetchMetrocorpHoldings = useCallback(() => {
        if (!metrocorpWidgetEnabled || metrocorpHoldingsInFlight.current) {
            return;
        }
        metrocorpHoldingsInFlight.current = true;
        setMetrocorpHoldingsLoading(true);
        metrocorpMiddleware.listHoldings({ includeWidgetAccounts: true }).then((response) => {
            if (response?.type === "W" || response?.data?.code === "API530W") {
                setMetrocorpHoldings({ widgetAccounts: [] });
            } else {
                setMetrocorpHoldings(response?.data?.data ?? response?.data ?? {});
            }
            metrocorpHoldingsInFlight.current = false;
            setMetrocorpHoldingsLoading(false);
        });
    }, [metrocorpWidgetEnabled]);

    useEffect(() => {
        if (!userId) {
            return;
        }
        if (multiSectionLayoutEntries.length > 0) {
            const firstId = multiSectionLayoutEntries[0].id;
            if (metrocorpWidgetEnabled && firstId === "metrocorp") {
                fetchMetrocorpHoldings();
            } else {
                dispatch(widgetsActions.listRequest(firstId, userId));
            }
            initialMultiSectionLoaded.current = true;
        }
    }, [dispatch, userId, multiSectionLayoutEntries, metrocorpWidgetEnabled, fetchMetrocorpHoldings]);

    useEffect(() => {
        if (desktopAvailableWidgets && desktopAvailableWidgets.length > 0) {
            setEditWidgets(mapAvailableWidgetsToEditItems(desktopAvailableWidgets, desktopLayout));
        }
    }, [desktopAvailableWidgets, desktopLayout]);

    const handleEditWidgetsChange = useCallback((items) => {
        setEditWidgets([...items]);
    }, []);

    const handleEditDesktopContinue = useCallback(() => {
        const selected = editWidgetsRef.current.filter((w) => w.selected);
        const newLayout = selected.map((w, idx) => ({
            id: w.id,
            uri: `widgets/${w.id}`,
            column: 1,
            row: idx,
        }));
        skipGetDataAfterEdit.current = true;
        dispatch(desktopActions.setLayout(newLayout));
        dispatch(desktopActions.saveLayoutRequest());
    }, [dispatch]);

    const baseUrl = configUtil.get("client.baseURL");

    const carouselItems = useMemo(
        () =>
            (campaigns || [])
                .map((campaign) => {
                    const imageUrl =
                        campaign?.imageUrl || (campaign?.image ? `data:image/png;base64,${campaign.image}` : "");
                    if (!imageUrl) {
                        return null;
                    }
                    const campaignUrl = campaign?.url || "";
                    const redirect =
                        campaignUrl && baseUrl && campaignUrl.includes(baseUrl)
                            ? campaignUrl.replace(baseUrl, "")
                            : campaignUrl || "#";
                    return { img: imageUrl, redirect };
                })
                .filter(Boolean),
        [campaigns, baseUrl],
    );

    const accounts = useMemo(() => mapAccountsForDashboard((widgetAccountsList || []).slice(0, maxItems)), [
        widgetAccountsList,
        maxItems,
    ]);

    const accountCbuMap = useMemo(() => {
        const map = {};
        (widgetAccountsList || []).forEach((account) => {
            if (account.cbu) {
                map[String(account.idProduct)] = account.cbu;
            }
        });
        return map;
    }, [widgetAccountsList]);

    const pendingTransactions = useMemo(() => mapPendingWidgetRowsToDashboard(widgetPendingList || [], maxItems), [
        widgetPendingList,
        maxItems,
    ]);

    const fundcorpAccountsCount = dataAccountsFundcorp?.accountsArray?.length || 0;

    const shortcuts = useMemo(
        () =>
            buildDashboardShortcuts({
                dispatch,
                activeEnvironment,
                isRetailEnvironment,
                fundcorpAccountsCount,
                currentPathname: location?.pathname ?? "/v3/dashboard",
            }),
        [dispatch, activeEnvironment, isRetailEnvironment, fundcorpAccountsCount, location?.pathname],
    );

    const greetingFirstName = useMemo(() => firstNameFromFullName(userFullName) || "", [userFullName]);

    const multiSectionNavigate = useMemo(
        () => ({
            pushEcheqDetail: (chequeId) => dispatch(push(`/echeqs/received/detail/${chequeId}`)),
            pushFunds: (cuenta, fondoNum) => {
                const opt = isEnabledApi5() ? "movementsAndRequests" : "movements";
                dispatch(push(`/fundcorpOperations?opt=${opt}&c=${cuenta}&f=${fondoNum}`));
            },
            pushDeposit: (idProduct) => dispatch(push(`/deposits/${idProduct}`)),
            pushFinancing: () => dispatch(push("/financing/categories")),
            pushMetrocorp: () => {
                const returnPath =
                    typeof location?.pathname === "string" && location.pathname.startsWith("/")
                        ? location.pathname
                        : "/v3/dashboard";
                dispatch(push({ pathname: getMetrocorpConsultPath(), state: { return: returnPath } }));
            },
            pushMetrocorpHoldings: (filters) => {
                dispatch(
                    push({
                        pathname: getMetrocorpConsultPath(),
                        state: { metrocorpHoldingsFilters: filters },
                    }),
                );
            },
        }),
        [dispatch, location?.pathname],
    );

    const metrocorpWidgetAccounts = useMemo(
        () =>
            mapMetrocorpHoldingsToWidgetAccounts(metrocorpHoldings, {
                comitenteLabel: i18n.get("metrocorp.widget.comitenteLabel"),
                comitenteShortLabel: i18n.get("metrocorp.widget.comitenteShortLabel"),
                comitenteHelper: i18n.get("metrocorp.widget.comitenteHelper"),
                accountLabel: i18n.get("metrocorp.operations.patrimonyAccountLabel"),
                asOf: (date) => i18n.get("metrocorp.operations.valuationDate", null, { date }),
                performanceTitle: (date) => i18n.get("metrocorp.operations.performanceTitle", null, { date }),
            }),
        [metrocorpHoldings],
    );

    const multiSectionSections = useMemo(
        () =>
            buildDashboardMultiSectionSections({
                layoutEntries: multiSectionLayoutEntries,
                widgetsState: multiSectionWidgets || {},
                maxItems,
                navigate: multiSectionNavigate,
                metrocorpWidget: metrocorpWidgetEnabled
                    ? {
                          accounts: metrocorpWidgetAccounts,
                          loading: metrocorpHoldingsLoading,
                          getLabels: (key) => i18n.get(`metrocorp.operations.${key}`),
                          onAccountNavigate: (accountId, verifyCode) =>
                              multiSectionNavigate.pushMetrocorpHoldings(
                                  metrocorpWidgetNavigation(accountId, verifyCode, metrocorpWidgetAccounts),
                              ),
                      }
                    : undefined,
            }),
        [
            multiSectionLayoutEntries,
            multiSectionWidgets,
            maxItems,
            multiSectionNavigate,
            metrocorpWidgetEnabled,
            metrocorpWidgetAccounts,
            metrocorpHoldingsLoading,
        ],
    );

    const multiSectionDefaultSectionKey = useMemo(() => {
        const first = multiSectionLayoutEntries[0];
        return first ? WIDGET_ID_TO_TITLE_KEY[first.id] : undefined;
    }, [multiSectionLayoutEntries]);

    const handleMultiSectionGetData = useCallback(
        (sectionKey) => {
            if (!initialMultiSectionLoaded.current || skipGetDataAfterEdit.current) {
                skipGetDataAfterEdit.current = false;
                return;
            }
            if (metrocorpWidgetEnabled && sectionKey === "metrocorp") {
                fetchMetrocorpHoldings();
                return;
            }
            const widgetId = TITLE_KEY_TO_WIDGET_ID[sectionKey];
            if (widgetId && userId) {
                dispatch(widgetsActions.listRequest(widgetId, userId));
            }
        },
        [dispatch, userId, metrocorpWidgetEnabled, fetchMetrocorpHoldings],
    );

    const handleMultiSectionGoToSection = useCallback(
        (sectionKey) => {
            if (sectionKey === "metrocorp" && metrocorpWidgetEnabled) {
                const hasAccounts = metrocorpWidgetAccounts.length > 0;
                dispatch(
                    push({
                        pathname: getMetrocorpConsultPath(),
                        state: {
                            metrocorpHoldingsFilters: {
                                accountId: hasAccounts ? undefined : null,
                                speciesId: "all",
                            },
                        },
                    }),
                );
                return;
            }
            const routes = {
                echeq: "/echeqs",
                funds: "/fundcorpOperations",
                deposits: "/deposits",
                financings: "/financing/categories",
                metrocorp: getMetrocorpConsultPath(),
            };
            const path = routes[sectionKey];
            if (path) {
                dispatch(push(path));
            }
        },
        [dispatch, metrocorpWidgetEnabled, metrocorpWidgetAccounts.length],
    );

    const dashboardAccountTransferEnabled = useMemo(() => {
        const perm = activeEnvironment?.permissions;
        if (!perm) {
            return false;
        }
        return Boolean(perm.transferThirdParties || perm.transferToContact || perm.transferInternal);
    }, [activeEnvironment]);

    const handleDashboardAccountTransfer = useCallback(
        (accountId) => {
            const perm = activeEnvironment?.permissions || {};
            const summary = dashboardTransferDebitSummary(accountId);
            if (perm.transferThirdParties) {
                dispatch(transferActions.loadExternalTransfer(summary));
                dispatch(push(getTransferPath("thirdParties")));
                return;
            }
            if (perm.transferToContact) {
                dispatch(transferActions.loadThirdPartiesTransfer(summary));
                dispatch(push(getTransferPath("toContact")));
                return;
            }
            if (perm.transferInternal) {
                dispatch(transferActions.loadTransfer(summary));
                dispatch(push(getTransferPath("internal")));
            }
        },
        [activeEnvironment, dispatch],
    );

    const fingerprintEnabled = configUtil.getBoolean("frontend.login.fingerprint.enabled");
    const pushNotificationEnabled = configUtil.getBoolean("frontend.show.pushNotification.functionalities");

    return (
        <>
            <Toast
                variant="warning"
                message={configUtil.get("frontend.notificar.mantenimiento.texto") || ""}
                visible={showWelcomeToast}
                onClose={handleCloseMaintenanceToast}
                onButtonClick={handleDisableToastNews}
                buttonText={i18n.get("desktop.maintenance.understood")}
            />
            <Dashboard
                location={location}
                userName={greetingFirstName}
                getGreetingTexts={v3UiKit.getGreetingTexts}
                getDashboardLabels={v3UiKit.getDashboardLabels}
                getDashboardTextParams={v3UiKit.getDashboardTextParams}
                hideAmounts={hideAmounts}
                accounts={accounts}
                loadingAccounts={loadingWidgetAccounts}
                shortcuts={shortcuts}
                carouselItems={carouselItems}
                pendingTransactions={pendingTransactions}
                loadingPendingTransactions={loadingWidgetPending}
                onGoToAccountsSection={() => dispatch(push(getRutPathAccount()))}
                onGoToPendingSection={() =>
                    dispatch(push({ pathname: "/pendingTransaction/list", state: { initialTab: "PENDING" } }))
                }
                onAccountCopyCbu={(accountId) => {
                    const cbu = accountCbuMap[accountId];
                    if (cbu && navigator.clipboard) {
                        navigator.clipboard.writeText(cbu);
                    }
                }}
                onAccountStatements={(accountId) => {
                    dispatch(
                        push({
                            pathname: getBankAccountDetailPath(accountId),
                            state: { tabSelected: 3 },
                        }),
                    );
                }}
                onAccountTransfer={dashboardAccountTransferEnabled ? handleDashboardAccountTransfer : undefined}
                onAccountClick={(accountId) => {
                    dispatch(push(getBankAccountDetailPath(accountId)));
                }}
                onPendingTransactionClick={(transactionId) => {
                    dispatch(push(`/transaction/${transactionId}`));
                }}
                goToPositionConsolidated={() => dispatch(push("/ConsolidatedPosition"))}
                editDesktopWidgets={editWidgets.length > 0 ? editWidgets : undefined}
                onEditDesktopWidgetsChange={handleEditWidgetsChange}
                onEditDesktopContinue={handleEditDesktopContinue}
                maxTabWidgets={5}
                multiSectionSections={multiSectionSections}
                multiSectionDefaultSectionKey={multiSectionDefaultSectionKey}
                onMultiSectionGetData={handleMultiSectionGetData}
                onMultiSectionGoToSection={handleMultiSectionGoToSection}
            />
            {fingerprintEnabled && isMobileNative ? <BiometricIdentificationV3 /> : null}
            {pushNotificationEnabled && isMobileNative && (deviceWithFingerprint || biometryCancelled) ? (
                <PushNotification />
            ) : null}
        </>
    );
}

DashboardV3.propTypes = {
    location: shape({
        pathname: string.isRequired,
        search: string,
        hash: string,
    }),
    dispatch: func.isRequired,
    hideBalance: bool,
    userFullName: string,
    userId: string,
    activeEnvironment: shape({ permissions: shape({}) }),
    isRetailEnvironment: bool,
    deviceWithFingerprint: bool,
    biometryCancelled: bool,
    widgetAccountsList: arrayOf(shape({})),
    widgetPendingList: arrayOf(shape({})),
    loadingWidgetAccounts: bool,
    loadingWidgetPending: bool,
    campaigns: arrayOf(shape({})),
    desktopLayout: arrayOf(shape({ id: string, column: number, row: number })),
    desktopAvailableWidgets: arrayOf(shape({ id: string })),
    multiSectionWidgets: shape({}),
    dataAccountsFundcorp: shape({}),
    isMobileNative: bool.isRequired,
    showMaintenanceToast: bool,
    isComeFromLogin: bool,
    daysToExpireCredential: shape({}),
    desktopCredentialsNotificationShowed: bool,
};

DashboardV3.defaultProps = {
    location: undefined,
    hideBalance: false,
    userFullName: "",
    userId: "",
    activeEnvironment: null,
    isRetailEnvironment: false,
    deviceWithFingerprint: false,
    biometryCancelled: false,
    widgetAccountsList: [],
    widgetPendingList: [],
    loadingWidgetAccounts: false,
    loadingWidgetPending: false,
    campaigns: [],
    desktopLayout: [],
    desktopAvailableWidgets: [],
    multiSectionWidgets: {},
    dataAccountsFundcorp: {},
    showMaintenanceToast: false,
    isComeFromLogin: false,
    daysToExpireCredential: {},
    desktopCredentialsNotificationShowed: false,
};

const mapStateToProps = (state) => {
    const accountsW = widgetsSelectors.getWidget(state, "accounts");
    const pendingW = widgetsSelectors.getWidget(state, "pendingTransactions");
    return {
        hideBalance: settingsSelectors.getUserData(state)?.hideBalance,
        userFullName: sessionSelectors.getUserFullName(state),
        userId: sessionSelectors.getUserId(state),
        activeEnvironment: sessionSelectors.getActiveEnvironment(state),
        isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
        widgetAccountsList: accountsW?.data?.accounts ?? [],
        widgetPendingList: pendingW?.data?.pendingTransactions ?? [],
        loadingWidgetAccounts: Boolean(accountsW?.isFetching),
        loadingWidgetPending: Boolean(pendingW?.isFetching),
        campaigns: campaignsSelectors.getCampaigns(state),
        desktopLayout: desktopSelectors.getLayout(state),
        desktopAvailableWidgets: desktopSelectors.getAvailableWidgets(state),
        deviceWithFingerprint: fingerprintSelectors.getIsDeviceWithFingerprint(state),
        biometryCancelled: pushNotificationsSelectors.biometryCancelled(state),
        dataAccountsFundcorp: accountsSelectors.getDataAccountsFundcorp(state),
        multiSectionWidgets: {
            echeqsReceived: widgetsSelectors.getWidget(state, "echeqsReceived"),
            funds: widgetsSelectors.getWidget(state, "funds"),
            deposits: widgetsSelectors.getWidget(state, "deposits"),
            financing: widgetsSelectors.getWidget(state, "financing"),
            metrocorp: widgetsSelectors.getWidget(state, "metrocorp"),
        },
        showMaintenanceToast: sessionSelectors.getShowMaintenanceToast(state),
        isComeFromLogin: statusSelectors.isComeFromLogin(state),
        daysToExpireCredential: loginSelectors.getDaysToExpireCredential(state),
        desktopCredentialsNotificationShowed: loginSelectors.getDesktopCredentialsNotificationShowed(state),
    };
};

export default compose(connect(mapStateToProps), resizableRoute)(DashboardV3);
